import { ReactElement, ReactNode, useEffect, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import {
  CapacityAlert,
  CapacityBand,
  CommissionerMatrix,
  CommissionerMatrixData,
  EmptyState,
  formatCents,
  formatShare,
  KpiCard,
  MobileGroupHeader,
  MobileMetric,
  MobileSubtotalCard,
  MobileTicketCard,
  MobileTotalCard,
  PaymentSummary,
  salesReportCopy,
  salesReportIcons,
  SalesTableGroup,
  SalesTableHeader,
  salesTableMinWidth,
  SalesTableRow,
  SalesTableScroll,
  SalesTableSubtotal,
  SalesTableTotal,
  SummarySort,
  SummaryTable,
  SummaryTableGroup,
  TruncatedNotice,
} from '../../../components/SalesReport'
import { SalesReportResponse } from '../../../hooks/sales-report'
import { nextSummarySort, SalesReportGroupView, SalesReportSummaryView, SalesReportView, sortSummaryGroups } from '../report'
import * as S from './styles'

interface ReportContentProps {
  report: SalesReportResponse
  view: SalesReportView
  mobile: boolean
  onExport?: () => void
  exportDisabled?: boolean
  summarySort?: SummarySort | null
  onSummarySortChange?: (sort: SummarySort | null) => void
}

const KPI_COMPACT_LENGTH = 12

function Kpis({ kpis }: { kpis: SalesReportResponse['kpis'] }): ReactElement {
  const issued = kpis.issued.toLocaleString('pt-BR')
  const revenue = formatCents(kpis.ticketRevenueCents)
  const courtesies = kpis.courtesies.toLocaleString('pt-BR')
  const compact = [issued, revenue, courtesies].some((value) => value.length > KPI_COMPACT_LENGTH)
  return (
    <S.Kpis>
      <KpiCard
        icon={salesReportIcons.ticket}
        label="Ingressos emitidos"
        value={issued}
        caption={`${kpis.issuedWithValue.toLocaleString('pt-BR')} com valor + ${courtesies} cortesias`}
        compact={compact}
      />
      <KpiCard
        icon={salesReportIcons.chartLine}
        label="Receita"
        value={revenue}
        caption="receita de ingressos"
        tone="positive"
        compact={compact}
      />
      <KpiCard
        icon={salesReportIcons.gift}
        label="Cortesias"
        value={courtesies}
        caption="bilhetes com valor R$ 0,00"
        compact={compact}
      />
    </S.Kpis>
  )
}

interface SummaryDesktopProps {
  view: SalesReportView
  summary: SalesReportSummaryView
  sort: SummarySort | null
  onSortChange: (sort: SummarySort | null) => void
}

function SummaryDesktop({ view, summary, sort, onSortChange }: SummaryDesktopProps): ReactElement {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const printing = usePrinting()
  const allExpanded = view.groups.length > 0 && view.groups.every((group) => expanded.has(group.key))

  const toggle = (key: string): void => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const groups: SummaryTableGroup[] = sortSummaryGroups(view.groups, sort).flatMap((group) =>
    group.summary === undefined
      ? []
      : [
          {
            key: group.key,
            label: group.label,
            values: group.summary,
            expanded: printing || expanded.has(group.key),
            onToggle: () => toggle(group.key),
            detail: (
              <>
                {group.rows.length > 0 && (
                  <SalesTableScroll columns={view.columns} ariaLabel={`Ingressos de ${group.label}`} inParentScroll>
                    <SalesTableHeader columns={view.columns} />
                    <div>
                      {group.rows.map((row, index) => (
                        <SalesTableRow key={row.key} row={row} columns={view.columns} index={index} />
                      ))}
                    </div>
                  </SalesTableScroll>
                )}
                {group.partial && <S.PartialNote>{salesReportCopy.partialGroup}</S.PartialNote>}
              </>
            ),
          },
        ]
  )

  return (
    <SummaryTable
      groupLabel={summary.groupLabel}
      columns={summary.columns}
      groups={groups}
      total={{ label: view.total.label, values: summary.total }}
      sort={sort}
      onSort={(key) => onSortChange(nextSummarySort(sort, key))}
      allExpanded={allExpanded}
      onToggleAll={() => setExpanded(allExpanded ? new Set() : new Set(view.groups.map((group) => group.key)))}
      detailMinWidth={salesTableMinWidth(view.columns)}
    />
  )
}

function DesktopTable({ view }: { view: SalesReportView }): ReactElement {
  return (
    <SalesTableScroll columns={view.columns} ariaLabel="Vendas do evento">
      <SalesTableHeader columns={view.columns} />
      <S.Groups>
        {view.groups.map((group) => (
          <SalesTableGroup key={group.key} header={group.header}>
            {group.rows.map((row, index) => (
              <SalesTableRow key={row.key} row={row} columns={view.columns} index={index} />
            ))}
            {group.subtotal && <SalesTableSubtotal subtotal={group.subtotal} columns={view.columns} />}
          </SalesTableGroup>
        ))}
        <SalesTableTotal total={view.total} columns={view.columns} />
      </S.Groups>
    </SalesTableScroll>
  )
}

export const MOBILE_CARDS_PER_BLOCK = 200

interface MobileGroupProps {
  group: SalesReportGroupView
  rows: SalesReportGroupView['rows']
  showFee: boolean
  summaryColumns?: SalesReportSummaryView['columns']
  expanded: boolean
  onToggle?: () => void
}

const groupMetrics = (group: SalesReportGroupView, columns: SalesReportSummaryView['columns'] | undefined): MobileMetric[] | undefined => {
  const summary = group.summary
  if (summary === undefined || columns === undefined) return undefined
  const metric = (label: string, cell: { text: string, hint?: string, low?: boolean }): MobileMetric => ({
    label,
    value: cell.text,
    ...(cell.hint === undefined ? {} : { hint: cell.hint }),
    ...(cell.low === true ? { low: true } : {}),
  })
  return [
    { label: 'Vendidos', value: summary.sold.toLocaleString('pt-BR') },
    { label: 'Cortesias', value: summary.courtesies.toLocaleString('pt-BR') },
    ...(columns.stock ? [metric('Capacidade', summary.capacity), metric('Disponível', summary.available)] : []),
    ...(columns.held ? [metric('Reservados', summary.held)] : []),
    { label: 'Ticket médio', value: summary.averageTicketCents === null ? '—' : formatCents(summary.averageTicketCents) },
  ]
}

const groupRevenue = (group: SalesReportGroupView): string | undefined => {
  if (group.subtotal == null) return undefined
  const share = group.subtotal.revenueShare
  return [formatCents(group.subtotal.totalCents), share == null ? null : salesReportCopy.revenueShare(formatShare(share))]
    .filter(Boolean)
    .join(' · ')
}

function MobileGroup({ group, rows, showFee, summaryColumns, expanded, onToggle }: MobileGroupProps): ReactElement {
  const complete = rows.length === group.rows.length
  const rowsId = useId()
  return (
    <S.MobileSection aria-label={group.header ? group.label : 'Vendas'}>
      {group.header && group.stock && (
        <MobileGroupHeader
          label={group.label}
          summary={group.stock.summary}
          metrics={onToggle ? groupMetrics(group, summaryColumns) : undefined}
          issuedPercentage={group.summary?.occupancy ?? group.stock.issuedPercentage}
          revenue={onToggle ? groupRevenue(group) : undefined}
          expanded={expanded}
          onToggle={onToggle}
          controlsId={rowsId}
        />
      )}
      <S.MobileGroupRows id={rowsId} hidden={!expanded}>
        {rows.map((row) => (
          <MobileTicketCard
            key={row.key}
            ticket={row.ticket}
            lot={row.lot}
            gender={row.gender}
            ticketType={row.ticketType}
            courtesy={row.courtesy}
            quantity={row.quantity}
            stock={row.stock}
            unitValueCents={row.unitValueCents}
            feeCents={row.feeCents}
            paymentMethod={row.paymentMethod}
            totalCents={row.totalCents}
          />
        ))}
        {complete && group.subtotal && (
          <MobileSubtotalCard
            label={group.subtotal.label}
            left={group.stock?.remainingText ? `${salesReportCopy.stockColumn}: ${group.stock.remainingText}` : undefined}
            right={`Emitidos: ${group.subtotal.quantity.toLocaleString('pt-BR')}`}
            totalLabel="Total:"
            totalCents={group.subtotal.totalCents}
            feeCents={showFee ? group.subtotal.feeCents : undefined}
          />
        )}
        {group.partial && <S.PartialNote>{salesReportCopy.partialGroup}</S.PartialNote>}
      </S.MobileGroupRows>
    </S.MobileSection>
  )
}

const usePrinting = (): boolean => {
  const [printing, setPrinting] = useState(false)
  useEffect(() => {
    const before = (): void => flushSync(() => setPrinting(true))
    const after = (): void => setPrinting(false)
    window.addEventListener('beforeprint', before)
    window.addEventListener('afterprint', after)
    return () => {
      window.removeEventListener('beforeprint', before)
      window.removeEventListener('afterprint', after)
    }
  }, [])
  return printing
}

function MobileCards({ view }: { view: SalesReportView }): ReactElement {
  const [budget, setBudget] = useState(MOBILE_CARDS_PER_BLOCK)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const printing = usePrinting()
  const statusRef = useRef<HTMLParagraphElement>(null)
  const focusWhenDone = useRef(false)
  const collapsible = view.groups.some((group) => group.header != null)
  const isOpen = (key: string): boolean => printing || !collapsible || expanded.has(key)
  const allOpen = view.groups.every((group) => expanded.has(group.key))
  const totalRows = view.groups.reduce((sum, group) => sum + (isOpen(group.key) ? group.rows.length : 0), 0)
  const limit = printing ? totalRows : Math.min(budget, totalRows)
  const hidden = totalRows - limit

  useEffect(() => {
    if (focusWhenDone.current && hidden === 0) {
      focusWhenDone.current = false
      statusRef.current?.focus()
    }
  }, [hidden])

  let available = limit
  const shown = view.groups
    .map((group) => {
      if (!isOpen(group.key)) return { group, rows: [] }
      const rows = group.rows.slice(0, Math.max(0, available))
      available -= rows.length
      return { group, rows }
    })
    .filter(({ rows }) => collapsible || rows.length > 0)

  const toggle = (key: string): void => {
    const index = view.groups.findIndex((group) => group.key === key)
    if (index >= 0 && !expanded.has(key)) {
      const openRowsBefore = view.groups
        .slice(0, index)
        .reduce((sum, group) => sum + (isOpen(group.key) ? group.rows.length : 0), 0)
      const firstBlock = Math.min(view.groups[index].rows.length, MOBILE_CARDS_PER_BLOCK)
      setBudget((current) => Math.max(current, openRowsBefore + firstBlock))
    }
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const toggleAll = (): void => {
    setExpanded(allOpen ? new Set() : new Set(view.groups.map((group) => group.key)))
  }

  const showMore = (): void => {
    const next = budget + MOBILE_CARDS_PER_BLOCK
    if (next >= totalRows) focusWhenDone.current = true
    setBudget(next)
  }

  return (
    <S.Groups>
      {collapsible && view.groups.length > 1 && (
        <S.ToggleAll type="button" data-print-hide="" onClick={toggleAll}>
          {allOpen ? 'Recolher todos' : 'Expandir todos'}
        </S.ToggleAll>
      )}
      {shown.map(({ group, rows }) => (
        <MobileGroup
          key={group.key}
          group={group}
          rows={rows}
          showFee={view.columns.showFee}
          summaryColumns={view.summary?.columns}
          expanded={isOpen(group.key)}
          onToggle={collapsible ? () => toggle(group.key) : undefined}
        />
      ))}
      {totalRows > MOBILE_CARDS_PER_BLOCK && (
        <S.MobileProgress ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
          {`Mostrando ${limit.toLocaleString('pt-BR')} de ${totalRows.toLocaleString('pt-BR')} linhas`}
        </S.MobileProgress>
      )}
      {hidden > 0 && (
        <S.ShowMore type="button" onClick={showMore}>
          {`Mostrar mais ${Math.min(hidden, MOBILE_CARDS_PER_BLOCK).toLocaleString('pt-BR')}`}
        </S.ShowMore>
      )}
      <MobileTotalCard
        label={view.total.label}
        quantity={view.total.quantity}
        totalCents={view.total.totalCents}
        feeCents={view.columns.showFee ? view.total.feeCents : undefined}
        stockText={view.total.stockText}
      />
    </S.Groups>
  )
}

function CommissionerView({ view, matrix, mobile }: { view: SalesReportView, matrix: CommissionerMatrixData, mobile: boolean }): ReactElement {
  const printing = usePrinting()
  const groups = new Map(view.groups.map((group) => [group.key, group] as [string, SalesReportGroupView]))
  const detail = (key: string): ReactNode => {
    const group = groups.get(key)
    if (group === undefined) return null
    return (
      <>
        {group.rows.length > 0 &&
          (mobile ? (
            <S.MobileGroupRows>
              {group.rows.map((row) => (
                <MobileTicketCard
                  key={row.key}
                  ticket={row.ticket}
                  lot={row.lot}
                  gender={row.gender}
                  ticketType={row.ticketType}
                  courtesy={row.courtesy}
                  quantity={row.quantity}
                  stock={row.stock}
                  unitValueCents={row.unitValueCents}
                  feeCents={row.feeCents}
                  paymentMethod={row.paymentMethod}
                  totalCents={row.totalCents}
                />
              ))}
            </S.MobileGroupRows>
          ) : (
            <SalesTableScroll columns={view.columns} ariaLabel={`Ingressos de ${group.label}`} inParentScroll>
              <SalesTableHeader columns={view.columns} />
              <div>
                {group.rows.map((row, index) => (
                  <SalesTableRow key={row.key} row={row} columns={view.columns} index={index} />
                ))}
              </div>
            </SalesTableScroll>
          ))}
        {group.partial && <S.PartialNote>{salesReportCopy.partialGroup}</S.PartialNote>}
      </>
    )
  }
  return (
    <>
      {view.paymentSummary && <PaymentSummary data={view.paymentSummary} expandAll={printing} />}
      <CommissionerMatrix data={matrix} mobile={mobile} expandAll={printing} renderDetail={detail} />
    </>
  )
}

export function ReportContent({
  report,
  view,
  mobile,
  onExport,
  exportDisabled = false,
  summarySort,
  onSummarySortChange,
}: ReportContentProps): ReactElement {
  const [localSort, setLocalSort] = useState<SummarySort | null>(null)
  const sort = summarySort === undefined ? localSort : summarySort
  const setSort = onSummarySortChange ?? setLocalSort
  return (
    <S.Content aria-label="Resultado do relatório">
      <Kpis kpis={report.kpis} />
      {view.band && <CapacityBand band={view.band} />}
      {report.meta.truncated && (
        <TruncatedNotice
          rowCount={report.meta.rowCount}
          totalRowCount={report.meta.totalRowCount}
          onExport={onExport}
          exportDisabled={exportDisabled}
        />
      )}
      {view.alert && <CapacityAlert message={view.alert} />}
      {view.columns.showStock && <S.StockNote>{salesReportCopy.stockNote}</S.StockNote>}
      {view.commissionerMatrix && <S.StockNote>{salesReportCopy.commissionerOnly}</S.StockNote>}
      {view.empty ? (
        <EmptyState />
      ) : view.commissionerMatrix ? (
        <CommissionerView key={report.meta.generatedAt} view={view} matrix={view.commissionerMatrix} mobile={mobile} />
      ) : mobile ? (
        <MobileCards key={report.meta.generatedAt} view={view} />
      ) : view.summary !== null ? (
        <SummaryDesktop key={report.meta.generatedAt} view={view} summary={view.summary} sort={sort} onSortChange={setSort} />
      ) : (
        <DesktopTable view={view} />
      )}
      {!view.empty && !view.commissionerMatrix && view.paymentSummary && <PaymentSummary data={view.paymentSummary} />}
    </S.Content>
  )
}
